import { z } from 'zod';
import type { Employee } from '@squadlab/contracts';
import { err, ok, type Result } from './result.js';

/**
 * Boundary parsing for employee records (ADR 0005 — parse, don't validate).
 *
 * Everything that enters the app as data-at-rest or over a wire (seed rows,
 * persisted-on-read, later LLM/API payloads) goes through here and comes out a
 * typed {@link Employee} or a classified `validation` failure. Contracts stay
 * parser-free; this module is the owning service's parser (ADR 0052 §4).
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected ISO YYYY-MM-DD');

const employeeSchema = z.object({
  id: z.string().min(1),
  ventureId: z.string().min(1),
  nameEn: z.string().min(1),
  nameAr: z.string().min(1),
  email: z.string().email().optional(),
  jobTitle: z.string().min(1),
  nationalId: z.string().min(1),
  gosiNumber: z.string().min(1).optional(),
  saudiNational: z.boolean(),
  contract: z.object({
    type: z.enum(['fixed-term', 'indefinite']),
    startDate: isoDate,
    endDate: isoDate.optional(),
    probationEndDate: isoDate.optional(),
  }),
  salary: z.object({
    basic: z.number().int().nonnegative(),
    housing: z.number().int().nonnegative(),
    transport: z.number().int().nonnegative(),
    other: z.number().int().nonnegative(),
  }),
  documents: z.array(
    z.object({
      kind: z.enum(['iqama', 'passport', 'contract', 'medical-insurance', 'certification']),
      reference: z.string().min(1).optional(),
      expiresOn: isoDate,
    }),
  ),
});

/** Parse one raw record into a typed Employee (Result, never a throw). */
export function parseEmployee(raw: unknown): Result<Employee> {
  const parsed = employeeSchema.safeParse(raw);
  if (!parsed.success) {
    return err('validation', 'Employee record failed boundary parsing', parsed.error);
  }
  return ok(parsed.data as Employee);
}

/** Parse a batch; the FIRST invalid record fails the whole read (no silent drops). */
export function parseEmployees(raw: readonly unknown[]): Result<readonly Employee[]> {
  const out: Employee[] = [];
  for (const row of raw) {
    const r = parseEmployee(row);
    if (!r.ok) return r;
    out.push(r.value);
  }
  return ok(out);
}
